// src/components/resources/tools/generators/SubtitleOverlay/html-export.ts
//
// Pure HTML-document builder harvested from <SubtitleOverlayOutput>. The former
// in-component `generateHTML` closed over `formData`; here it takes it as an
// explicit argument so the markup is unit-testable in isolation. The position
// CSS now comes from the shared `getPositionCss` resolver. Logic + markup are a
// verbatim move.
import { safeString } from '~/lib/utils/formatters'
import { getPositionCss } from './position-styles'

/**
 * Generates downloadable HTML content
 */
export function generateHTML(formData: Record<string, unknown>): string {
  // Create a complete HTML document with the subtitle overlay
  const rawSubtitleText = safeString(formData.subtitleText) || 'Enter your subtitle text here...'
  // Trim whitespace and normalize line breaks to prevent layout issues
  const subtitleText = rawSubtitleText.trim().replace(/\s+/g, ' ')
  const fontSize = Number(formData.fontSize) || 18
  const fontFamily = safeString(formData.fontFamily) || 'Arial, sans-serif'
  const fontWeight = safeString(formData.fontWeight) || 'normal'
  const lineHeight = Number(formData.lineHeight) || 1.4
  const textColor = safeString(formData.textColor) || '#ffffff'
  const subtitleBackgroundColor = safeString(formData.backgroundColor) || '#000000'
  const backgroundOpacity = Number(formData.backgroundOpacity) || 0.75
  const textShadow = safeString(formData.textShadow) || 'none'
  const borderRadius = Number(formData.borderRadius) || 4
  const textAlignment = safeString(formData.textAlignment) || 'center'
  const maxWidth = formData.maxWidth !== undefined ? Number(formData.maxWidth) : 90
  const position = safeString(formData.position) || 'bottom-center'
  const margin = formData.margin !== undefined ? Number(formData.margin) : 40

  // Determine position style
  const positionCSS = getPositionCss(position, margin)

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Subtitle Overlay</title>
    <style>
        body {
            margin: 0;
            padding: 0;
            width: 100vw;
            height: 100vh;
            overflow: hidden;
            background: transparent;
        }
        .subtitle-overlay {
            position: absolute;
            ${positionCSS}
            max-width: ${Math.min(Math.max(maxWidth, 10), 100)}%;
            width: ${Math.min(Math.max(maxWidth, 10), 100)}%;
            text-align: ${textAlignment};
            z-index: 1000;
        }
        .subtitle-text {
            display: inline-block;
            padding: 8px 16px;
            font-family: ${fontFamily};
            font-size: ${fontSize}px;
            font-weight: ${fontWeight};
            line-height: ${lineHeight};
            color: ${textColor};
            background-color: ${subtitleBackgroundColor}${Math.round(backgroundOpacity * 255).toString(16).padStart(2, '0')};
            text-shadow: ${textShadow};
            border-radius: ${borderRadius}px;
            word-wrap: break-word;
            overflow-wrap: break-word;
            box-sizing: border-box;
            width: 100%;
            white-space: pre-wrap;
        }
    </style>
</head>
<body>
    <div class="subtitle-overlay">
        <div class="subtitle-text">${subtitleText}</div>
    </div>
</body>
</html>`

  return html
}
